import React from 'react';
import { Select, Space, Typography, Tag, Tooltip } from 'antd';
import { MapPin, Building2, ChevronDown, Lock } from 'lucide-react';
import { useBranchContext } from 'hooks/useBranchContext';
import { useLanguage } from 'hooks/Languages/useLanguage';
import { useFetchUserData } from 'queries/auth/user';
import { isUserAdminOrOwner } from 'utils/roleConfig';

const { Text } = Typography;

export const BranchSwitcher: React.FC = () => {
  const { branchId, branches, switchBranch, isLoading, branchName } = useBranchContext();
  const { translate } = useLanguage();
  const { userData } = useFetchUserData();

  const isOwnerOrAdmin = React.useMemo(() => isUserAdminOrOwner(userData), [userData]);

  if (isLoading && (!branches || branches.length === 0)) {
    return (
      <div className="branch-switcher-container">
        <Select
          loading
          disabled
          value={translate('Loading branches...')}
          className="branch-select"
          bordered={false}
          style={{ width: '100%', opacity: 0.7 }}
        />
      </div>
    );
  }

  if (!branches || branches.length <= 1) return null;

  // 🛡️ Lock branch switching for non-admin / non-owner roles
  if (!isOwnerOrAdmin) {
    const assignedBranch = branches.find((b: any) => String(b.id) === String(branchId)) || branches[0];
    return (
      <div className="branch-switcher-container">
        <Tooltip title={translate('Branch locked to your assigned branch')}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 12px',
              borderRadius: 8,
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              cursor: 'not-allowed',
            }}
          >
            <Lock size={14} style={{ color: '#64748b' }} />
            <Text strong style={{ fontSize: 13, color: '#334155' }}>
              {assignedBranch?.name || branchName || translate('Branch Locked')}
            </Text>
          </div>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="branch-switcher-container">
      <Tooltip title={translate('Switch Branch')}>
        <Select
          value={branches.some((b: any) => b.id === branchId) ? branchId : undefined}
          placeholder={branchName || translate('Select Branch')}
          onChange={switchBranch}
          className="branch-select"
          suffixIcon={<ChevronDown size={14} />}
          bordered={false}
          dropdownStyle={{ minWidth: 250, borderRadius: 12, padding: 8 }}
          style={{ width: '100%' }}
          loading={isLoading}
          optionLabelProp="label"
        >
          {branches.map((branch: any) => (
            <Select.Option key={branch.id} value={branch.id} label={branch.name}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  backgroundColor: branch.is_main ? '#eff6ff' : '#f8fafc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: branch.is_main ? '#3b82f6' : '#64748b'
                }}>
                  {branch.is_main ? <Building2 size={16} /> : <MapPin size={16} />}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Space size={4}>
                    <Text strong style={{ fontSize: 13, color: '#1e293b' }}>
                      {branch.name}
                    </Text>
                    {branch.is_main && (
                      <Tag color="blue" bordered={false} style={{ fontSize: 9, borderRadius: 4, margin: 0 }}>
                        {translate('MAIN')}
                      </Tag>
                    )}
                  </Space>
                  {branch.location && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {branch.location}
                    </Text>
                  )}
                </div>
              </div>
            </Select.Option>
          ))}
        </Select>
      </Tooltip>
    </div>
  );
};
